import * as THREE from 'three'
import { rememberAuthoredTransparency, setMaterialOpacity } from '@/utils/materialOpacity'
import { setMaterialTint } from '@/utils/materialTint'

const HOVER_EMISSIVE = 0x4a90d9
const HOVER_INTENSITY_BOOST = 0.35
const SELECT_EMISSIVE = 0xff7a00
const SELECT_INTENSITY = 1.1

type Appearance = {
  base: THREE.Material | THREE.Material[]
  opacity: number
  tint: number | null
  hovered: boolean
  selected: boolean
}

export type AppearanceChange = Partial<Omit<Appearance, 'base'>>

const appearances = new WeakMap<THREE.Mesh, Appearance>()
const variantsByBase = new WeakMap<THREE.Material, Map<string, THREE.Material>>()

function variantOf(base: THREE.Material, look: Omit<Appearance, 'base'>): THREE.Material {
  if (!(base instanceof THREE.MeshStandardMaterial)) return base
  const key = `${look.opacity}|${look.tint}|${look.hovered}|${look.selected}`
  let variants = variantsByBase.get(base)
  if (!variants) {
    variants = new Map()
    variantsByBase.set(base, variants)
  }
  let variant = variants.get(key)
  if (variant) return variant

  const material = base.clone()
  rememberAuthoredTransparency(material)
  setMaterialTint(material, look.tint)
  if (look.selected) {
    material.emissive.setHex(SELECT_EMISSIVE)
    material.emissiveIntensity = SELECT_INTENSITY
  } else if (look.hovered) {
    if (material.emissiveIntensity < 0.2) material.emissive.setHex(HOVER_EMISSIVE)
    material.emissiveIntensity += HOVER_INTENSITY_BOOST
  }
  setMaterialOpacity(material, look.selected ? 1 : look.opacity)
  variant = material
  variants.set(key, variant)
  return variant
}

export function appearanceOf(mesh: THREE.Mesh): Appearance {
  return (
    appearances.get(mesh) ?? {
      base: mesh.material,
      opacity: 1,
      tint: null,
      hovered: false,
      selected: false,
    }
  )
}

export function setMeshAppearance(mesh: THREE.Mesh, change: AppearanceChange) {
  const next = { ...appearanceOf(mesh), ...change }
  appearances.set(mesh, next)
  mesh.material = Array.isArray(next.base)
    ? next.base.map((m) => variantOf(m, next))
    : variantOf(next.base, next)
}

const HIGHLIGHT_LOOKS: Omit<Appearance, 'base'>[] = [
  { opacity: 1, tint: null, hovered: false, selected: true },
  { opacity: 1, tint: null, hovered: true, selected: false },
  { opacity: 0.5, tint: null, hovered: true, selected: false },
]

export function highlightWarmupMeshes(meshes: Iterable<THREE.Mesh>): THREE.Mesh[] {
  const geometryByBase = new Map<THREE.Material, THREE.BufferGeometry>()
  for (const mesh of meshes) {
    const base = appearanceOf(mesh).base
    for (const material of Array.isArray(base) ? base : [base]) {
      if (!geometryByBase.has(material)) geometryByBase.set(material, mesh.geometry)
    }
  }
  const warmup: THREE.Mesh[] = []
  for (const [base, geometry] of geometryByBase) {
    if (!(base instanceof THREE.MeshStandardMaterial)) continue
    for (const look of HIGHLIGHT_LOOKS) warmup.push(new THREE.Mesh(geometry, variantOf(base, look)))
  }
  return warmup
}

/** The authored materials of these meshes plus every variant derived from them. */
export function materialsToDispose(meshes: Iterable<THREE.Mesh>): Set<THREE.Material> {
  const materials = new Set<THREE.Material>()
  for (const mesh of meshes) {
    const base = appearances.get(mesh)?.base ?? mesh.material
    for (const material of Array.isArray(base) ? base : [base]) {
      materials.add(material)
      for (const variant of variantsByBase.get(material)?.values() ?? []) materials.add(variant)
    }
  }
  return materials
}
